import React, { useEffect, useMemo, useState } from 'react';
import { FiActivity, FiLink, FiRefreshCcw, FiSlash, FiX } from 'react-icons/fi';
import { createScalePairingCode, deleteScaleBinding } from '../api/scaleApi';
import { notify } from '../utils/alertBus';

const formatWeight = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return `${numeric.toFixed(3)} kg`;
};

const formatTimestamp = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString('es-CL', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const formatTransmissionMode = (value) => {
  if (value === 'stable_non_zero') return 'Estable / no-cero';
  if (value === 'continuous') return 'Continua estabilizada';
  return null;
};

const ScaleStatusPanel = ({
  branchId,
  cashRegisterId,
  cashRegisterSessionId,
  isSessionOwnedByUser,
  scaleState,
}) => {
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isPairingModalOpen, setIsPairingModalOpen] = useState(false);
  const [pairingCode, setPairingCode] = useState('');
  const [pairingExpiresAt, setPairingExpiresAt] = useState(null);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [isUnpairing, setIsUnpairing] = useState(false);

  useEffect(() => {
    if (!cashRegisterSessionId || !isSessionOwnedByUser) {
      setIsPairingModalOpen(false);
      setPairingCode('');
      setPairingExpiresAt(null);
    }
  }, [cashRegisterSessionId, isSessionOwnedByUser]);

  useEffect(() => {
    if (!isStatusModalOpen) {
      setIsPairingModalOpen(false);
    }
  }, [isStatusModalOpen]);

  const statusMeta = useMemo(() => {
    if (!cashRegisterId) {
      return {
        label: 'Selecciona una caja para habilitar balanza.',
        panelTone: 'bg-gray-50 border-gray-200 text-gray-600',
        triggerTone: 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50',
        dotTone: 'bg-gray-400',
      };
    }
    if (!cashRegisterSessionId) {
      return {
        label: 'Abre una sesion de caja para usar balanza.',
        panelTone: 'bg-gray-50 border-gray-200 text-gray-600',
        triggerTone: 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50',
        dotTone: 'bg-gray-400',
      };
    }
    if (!isSessionOwnedByUser) {
      return {
        label: 'La caja activa pertenece a otro usuario.',
        panelTone: 'bg-amber-50 border-amber-200 text-amber-700',
        triggerTone: 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100',
        dotTone: 'bg-amber-500',
      };
    }
    if (scaleState?.status === 'connected' && scaleState?.connectionStatus === 'connected') {
      return {
        label: 'Balanza conectada',
        panelTone: 'bg-emerald-50 border-emerald-200 text-emerald-700',
        triggerTone: 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
        dotTone: 'bg-emerald-500',
      };
    }
    if (scaleState?.status === 'disconnected' || scaleState?.connectionStatus === 'disconnected') {
      return {
        label: 'Balanza emparejada pero desconectada',
        panelTone: 'bg-amber-50 border-amber-200 text-amber-700',
        triggerTone: 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100',
        dotTone: 'bg-amber-500',
      };
    }
    if (scaleState?.status === 'paired') {
      return {
        label: 'Balanza emparejada, esperando lectura',
        panelTone: 'bg-blue-50 border-blue-200 text-blue-700',
        triggerTone: 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100',
        dotTone: 'bg-blue-500',
      };
    }
    if (scaleState?.status === 'registered' || scaleState?.registeredDevice) {
      return {
        label: 'Equipo autorizado para esta caja',
        panelTone: 'bg-blue-50 border-blue-200 text-blue-700',
        triggerTone: 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100',
        dotTone: 'bg-blue-500',
      };
    }
    if (scaleState?.connectionStatus === 'connecting') {
      return {
        label: 'Conectando balanza',
        panelTone: 'bg-blue-50 border-blue-200 text-blue-700',
        triggerTone: 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100',
        dotTone: 'bg-blue-500',
      };
    }
    return {
      label: 'Sin balanza emparejada',
      panelTone: 'bg-gray-50 border-gray-200 text-gray-600',
      triggerTone: 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50',
      dotTone: 'bg-gray-400',
    };
  }, [
    cashRegisterId,
    cashRegisterSessionId,
    isSessionOwnedByUser,
    scaleState?.connectionStatus,
    scaleState?.registeredDevice,
    scaleState?.status,
  ]);

  const canManageScale = Boolean(branchId && cashRegisterId && cashRegisterSessionId && isSessionOwnedByUser);
  const latestWeightLabel = formatWeight(scaleState?.latestStableWeightKg);
  const latestWeightAt = formatTimestamp(scaleState?.latestStableAt);
  const deviceName = scaleState?.binding?.deviceName || scaleState?.registeredDevice?.deviceName;
  const transmissionModeLabel = formatTransmissionMode(scaleState?.transmissionMode);

  const handleGenerateCode = async () => {
    if (!canManageScale || isGeneratingCode) return;
    setIsGeneratingCode(true);
    try {
      const response = await createScalePairingCode(branchId, cashRegisterId);
      setPairingCode(response?.code || '');
      setPairingExpiresAt(response?.expiresAt || null);
      setIsPairingModalOpen(true);
    } catch (error) {
      notify(error?.response?.data?.message || 'No se pudo generar el codigo de emparejamiento.');
    } finally {
      setIsGeneratingCode(false);
    }
  };

  const handleUnpair = async () => {
    if (!canManageScale || !(scaleState?.binding?.id || scaleState?.registeredDevice?.id) || isUnpairing) return;
    setIsUnpairing(true);
    try {
      await deleteScaleBinding(branchId, cashRegisterId);
      notify('Balanza desvinculada.');
      setPairingCode('');
      setPairingExpiresAt(null);
      setIsPairingModalOpen(false);
    } catch (error) {
      notify(error?.response?.data?.message || 'No se pudo desvincular la balanza.');
    } finally {
      setIsUnpairing(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsStatusModalOpen(true)}
        className={`inline-flex h-7 w-7 items-center justify-center rounded-full border transition ${statusMeta.triggerTone}`}
        title={`Balanza: ${statusMeta.label}`}
        aria-label={`Abrir estado de balanza: ${statusMeta.label}`}
      >
        <span className={`mr-1 inline-block h-2 w-2 rounded-full ${statusMeta.dotTone}`} />
        <FiActivity size={13} />
      </button>

      {isStatusModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          onClick={() => setIsStatusModalOpen(false)}
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Balanza</div>
                <h3 className="mt-1 text-lg font-semibold text-gray-900">Estado y configuracion</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsStatusModalOpen(false)}
                className="rounded-full border border-gray-200 p-2 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                aria-label="Cerrar modal de balanza"
              >
                <FiX size={16} />
              </button>
            </div>

            <div className={`mt-4 rounded-lg border px-3 py-3 ${statusMeta.panelTone}`}>
              <div className="text-[11px] font-semibold uppercase tracking-wide">Estado actual</div>
              <div className="mt-1 text-sm font-semibold">{statusMeta.label}</div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Equipo</div>
                <div className="mt-1 text-sm font-semibold text-gray-800 break-words">{deviceName || 'Sin equipo vinculado'}</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Flujo</div>
                <div className="mt-1 text-sm font-semibold text-gray-800">{transmissionModeLabel || 'Sin informacion'}</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Ultima lectura estable</div>
                <div className="mt-1 text-sm font-semibold text-gray-800">
                  {latestWeightLabel ? `${latestWeightLabel}${latestWeightAt ? ` a las ${latestWeightAt}` : ''}` : 'Sin lectura estable'}
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Puerto</div>
                <div className="mt-1 text-sm font-semibold text-gray-800">{scaleState?.lastSourcePort || 'Sin puerto reportado'}</div>
              </div>
            </div>

            {scaleState?.lastError && canManageScale && (
              <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700">
                {scaleState.lastError}
              </div>
            )}

            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={handleGenerateCode}
                disabled={!canManageScale || isGeneratingCode}
                className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold ${
                  !canManageScale || isGeneratingCode
                    ? 'cursor-not-allowed bg-gray-200 text-gray-400'
                    : 'border border-blue-200 bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {pairingCode ? <FiRefreshCcw size={14} /> : <FiLink size={14} />}
                {pairingCode ? 'Regenerar codigo' : 'Vincular equipo'}
              </button>
              <button
                type="button"
                onClick={handleUnpair}
                disabled={!canManageScale || !(scaleState?.binding?.id || scaleState?.registeredDevice?.id) || isUnpairing}
                className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold ${
                  !canManageScale || !(scaleState?.binding?.id || scaleState?.registeredDevice?.id) || isUnpairing
                    ? 'cursor-not-allowed bg-gray-200 text-gray-400'
                    : 'border border-red-200 bg-white text-red-700 hover:bg-red-50'
                }`}
              >
                <FiSlash size={14} />
                Desautorizar
              </button>
            </div>
          </div>
        </div>
      )}

      {isPairingModalOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4"
          onClick={() => setIsPairingModalOpen(false)}
        >
          <div
            className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900">Emparejar balanza</h3>
            <p className="mt-2 text-sm text-gray-600">
              Usa este codigo una sola vez en la app de escritorio del PC de la balanza para dejarlo autorizado en esta caja.
            </p>
            <div className="mt-4 rounded-lg bg-gray-900 px-4 py-4 text-center font-mono text-2xl tracking-[0.2em] text-white">
              {pairingCode || '------'}
            </div>
            {pairingExpiresAt && (
              <p className="mt-3 text-xs text-gray-500">
                Expira a las {new Date(pairingExpiresAt).toLocaleTimeString('es-CL', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}
              </p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-gray-200 px-3 py-2 text-gray-700"
                onClick={() => setIsPairingModalOpen(false)}
              >
                Cerrar
              </button>
              <button
                type="button"
                className={`rounded-lg px-3 py-2 text-white ${
                  isGeneratingCode
                    ? 'cursor-not-allowed bg-blue-300'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
                onClick={handleGenerateCode}
                disabled={isGeneratingCode}
              >
                Regenerar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ScaleStatusPanel;
